<template>
  <div class="RTEAO-situation" ref="situationRef">
    <div class="RTEAO-situation_left">
      <BorderWrap
        :title="`检出病原体次数`"
        @click="handlePathogenStatByDayBarClick"
        :options="pathogenStatByDayBar.options"
      />
      <BorderWrap :title="`节点检测结果数量`" :options="regionPathogenStatByHalfYearLine.options" />
    </div>

    <RightButton ref="rightButtonRef" :mode="mode" />

    <div class="RTEAO-situation_middle">
      <TabWrap
        v-for="item in midOptions"
        :key="item.prop"
        :label="item.label"
        :icon="item.icon"
        :value="midModel[item.prop]"
      />
    </div>
    <MapLibre @loaded="mapLoaded" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import MapLibre from '@/components/maplibre';
import { useDialog } from '@/components/dialog';

import TabWrap from '../components/tab-wrap.vue';
import BorderWrap from '../components/border-wrap.vue';
import NodeDetails from '../components/node-details.vue';
import NodeMarker from '../components/node-marker.vue';

import RightButton from '../components/RightButton/index.vue';

import {
  getOfficesState,
  getPathogenCountByDay,
  getPathogenCountByOffice,
  getSampleCountByOffice
} from '@/api/RTEAO';

import { bmap2CGCS2000 } from '@/utils/bmapUtil';
import { makeBarOptions, makeLineOptions } from './utils';
import { formatTime, toTimestamp } from '@/utils/date';
import { genComponentEl, objTransfer } from '@/utils/utils';
import { isEmpty } from '@/utils/is';
import { diffObjects } from '@/utils/utils';
import { cloneDeep } from 'lodash';

export default {
  components: {
    MapLibre,
    BorderWrap,
    RightButton,
    TabWrap
  },
  inject: ['isManagePathogenMap', 'model', 'timeRangeDict', 'resetAllParams'],
  props: {
    mode: {
      type: String,
      default: 'monitor'
    }
  },
  data() {
    return {
      loaded: false,
      old: null,
      midModel: {
        detectCount: 0,
        pathogenCount: 0
      },
      officesStateMap: {},
      midOptions: [
        {
          icon: 'icon-headset',
          label: '检测结果数量',
          prop: 'detectCount'
        },
        {
          icon: 'icon-detection',
          label: '检出病原体次数',
          prop: 'pathogenCount'
        }
      ],
      pathogenStatByDayData: [],
      pathogenStatByDayBar: makeBarOptions(),
      pathogenStatByHalfYearData: [],
      pathogenStatByHalfYearLine: makeLineOptions(),
      regionPathogenStatByHalfYearData: [],
      regionPathogenStatByHalfYearLine: makeBarOptions()
    };
  },
  computed: {
    ...mapState('socket', ['deviceIdMap']),
    timeRange() {
      return this.model.timeRange;
    },
    tips() {
      const label = this.timeRangeDict[this.model.timeRangeFlag].label;

      return this.mode !== 'review' ? `(${label})` : '';
    }
  },
  methods: {
    handlePathogenStatByDayBarClick(series) {
      const { pathogenType, pathogenName } = series.data;

      this.showDialog({
        pathogenType,
        pathogenName
      });
    },
    async showDialog({ pathogenType, pathogenName }) {
      const lineRes = await getPathogenCountByDay({
        beginTimestamp: this.timeRange[0],
        endTimestamp: this.timeRange[1],
        pathogenTypes: [pathogenType],
        officeIds: this.model.nodeList
      });

      const dimensions = new Set();
      dimensions.add('date');
      this.pathogenStatByHalfYearData.splice(
        0,
        Infinity,
        ...lineRes.data.map(e => ({
          date: e.date,
          list: Object.keys(e.pathogen).map(key => {
            return { name: key, value: e.pathogen[key] };
          })
        }))
      );

      let data = this.pathogenStatByHalfYearData
        .map(e => {
          const map = {
            date: e.date
          };

          e.list.forEach(e => {
            dimensions.add(e.name);
            map[e.name] = e.value;
          });

          return map;
        })
        .sort((a, b) => toTimestamp(a.date) - toTimestamp(b.date));

      // data = data.filter(e => e[pathogenName] > 0);

      if (toTimestamp(data[0].date) !== this.timeRange[0]) {
        data.unshift({
          date: formatTime(this.timeRange[0], 'yyyy-MM-dd'),
          [pathogenName]: ''
        });
      }

      if (toTimestamp(data.slice(-1)[0].date) !== this.timeRange[1]) {
        data.push({
          date: formatTime(this.timeRange[1], 'yyyy-MM-dd'),
          [pathogenName]: ''
        });
      }

      this.pathogenStatByHalfYearLine.setData(data, {
        color: ['#CCCCCC'],
        legend: { show: false },
        tooltip: {
          formatter: params => {
            try {
              const value = params[0].value;

              return `${value.date} <br/> ${pathogenName}: ${value[pathogenName]}`;
            } catch (error) {
              console.log(error);

              return '';
            }
          }
        },
        grid: {
          top: 10,
          bottom: 40
        },
        dataZoom: [
          {
            type: 'inside'
          },
          {
            handleIcon:
              'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
            handleSize: '80%',
            handleStyle: {
              color: '#fff',
              shadowBlur: 3,
              shadowColor: 'rgba(0, 0, 0, 0.6)',
              shadowOffsetX: 2,
              shadowOffsetY: 2
            }
          }
        ],
        xAxis: {
          type: 'time',
          axisLabel: {
            showMinLabel: true,
            showMaxLabel: true
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '',
            type: 'scatter',
            symbolSize: 15
          }
        ],
        // xAxis: {
        //   type: 'time',
        //   boundaryGap: false
        // },
        // series: [
        //   {
        //     type: 'line',
        //     smooth: false
        //   }
        // ],
        dataset: {
          dimensions: [...dimensions]
        }
      });

      useDialog({
        title: pathogenName,
        width: '70vw',
        marginTop: '25vh',
        appendToBody: false,
        container: this.$refs.situationRef,
        style: 'border: 1px solid rgba(12, 106, 182, 0.8);',
        modal: false,
        slots: {
          default: () => (
            <div style="width: 100%; height: 50vh;">
              <v-chart options={this.pathogenStatByHalfYearLine.options} autoresize={true} />
            </div>
          )
        }
      });
    },
    mapLoaded(mapApp) {
      this.mapApp = mapApp;

      this.$nextTick(async () => {
        const nodeList = this.model.nodeList;

        // 百度坐标(BD09) → CGCS2000(WGS84)
        const coords = nodeList.map(e => {
          const { longitude, latitude } = this.deviceIdMap[e];

          return bmap2CGCS2000([longitude, latitude]);
        });

        // 启用滚轮缩放
        mapApp.enableScrollZoom();

        // 延迟飞行到坐标范围（模拟原 bmapUtil.to 的 delay 行为）
        setTimeout(() => {
          mapApp.fitCoordinates(coords, { padding: 100, maxZoom: 14 });
        }, 2000);

        this.loaded = true;

        await this.getOfficesState();

        this.initNodes(nodeList);
      });
    },
    createNode({ id, name }) {
      const el = genComponentEl(NodeMarker, {
        title: name,
        nodeId: id,
        state: this.officesStateMap[id],
        onClick: () => {
          this.showNodeDetails({ id, name });
        }
      });

      return el;
    },
    showNodeDetails({ id, name }) {
      useDialog({
        title: `节点详情 (${name})`,
        width: '90vw',
        marginTop: '18vh',
        appendToBody: false,
        container: this.$refs.situationRef,
        modal: false,
        slots: {
          default: () => (
            <NodeDetails
              id={id}
              params={{
                timeRange: this.timeRange,
                pathogenList: this.model.pathogenList
              }}
            />
          )
        }
      });
    },
    initNodes(nodes) {
      const data = nodes
        .map(id => {
          const { longitude, latitude, ...rest } = this.deviceIdMap[id];

          // 百度坐标(BD09) → CGCS2000(WGS84)
          return {
            coordinate: bmap2CGCS2000([longitude, latitude]),
            latitude,
            ...rest
          };
        })
        .sort((a, b) => b.latitude - a.latitude);

      // 使用 MapApp 的 addHtmlMarkers 替代 makeHtmlLayer
      this.mapApp.addHtmlMarkers(data, item => this.createNode(item), {
        offsetY: 46
      });

      this.addPointLayer(data);
    },
    async getOfficesState() {
      try {
        const res = await getOfficesState({
          // officeIds: this.model.nodeList,
          beginTimestamp: this.timeRange[0],
          endTimestamp: this.timeRange[1],

          pathogenTypes: this.model.pathogenList
        });

        if (res.code !== 200) return;

        objTransfer(this.officesStateMap, res.data);
      } catch (error) {
        console.log(error);
      }
    },
    async initPathogenCountChart() {
      try {
        const res = await getPathogenCountByOffice({
          beginTimestamp: this.timeRange[0],
          endTimestamp: this.timeRange[1],
          officeIds: this.model.nodeList,
          pathogenTypes: this.model.pathogenList
        });

        this.pathogenStatByDayData.splice(
          0,
          Infinity,
          ...res.data.sort(
            (a, b) =>
              ~~this.isManagePathogenMap[b.pathogenType] -
              ~~this.isManagePathogenMap[a.pathogenType]
          )
        );

        const legends = [
          {
            name: '非列管',
            color: '#f3f367',
            right: '10'
          },
          {
            name: '列管',
            color: '#f4a310',
            right: '130'
          }
        ];

        const dict = [
          { lte: 2, rotate: 0, grid: { bottom: 20 } },
          { lte: 3, rotate: 15, grid: { bottom: 20 } },
          { lte: 5, rotate: 25 },
          { lte: 12, rotate: 35 },
          { lte: 15, rotate: 45 },
          { lte: 55, rotate: 90 }
        ];

        const len = this.pathogenStatByDayData.length;

        const { rotate, grid } = dict.find(e => e.lte >= len);

        this.pathogenStatByDayBar.setData(this.pathogenStatByDayData, {
          legend: { show: false },
          graphic: legends.map(e => ({
            type: 'group',
            right: e.right,
            top: '1%',
            children: [
              {
                type: 'rect',
                z: 100,
                left: '0',
                top: 'center',
                shape: {
                  width: 35,
                  height: 16,
                  r: [5]
                },
                style: {
                  fill: e.color,
                  stroke: '#555',
                  lineWidth: 0,
                  shadowBlur: 4,
                  shadowOffsetX: 0,
                  shadowOffsetY: 0,
                  shadowColor: 'rgba(0,0,0,0.3)'
                }
              },
              {
                type: 'text',
                z: 100,
                left: '50',
                top: 'middle',
                style: {
                  fill: e.color,
                  text: e.name,
                  font: '20px Microsoft YaHei'
                }
              }
            ]
          })),
          dataset: {
            dimensions: ['pathogenName', 'value']
          },
          grid: grid || { bottom: 0 },
          xAxis: {
            axisLabel: {
              rotate
            }
          },
          visualMap: {
            show: false,
            dimension: 0,
            pieces: this.pathogenStatByDayData.map((e, i) => ({
              lte: i,
              color: this.isManagePathogenMap[e.pathogenType] ? '#f4a310' : '#f3f367'
            }))
          },
          series: [
            {
              type: 'bar',
              name: '数量',
              barMaxWidth: 20,
              barMinWidth: 20
            }
          ]
        });
      } catch (error) {
        console.log(error);
      }
    },
    async initRegionPathogenStatChart() {
      try {
        const res = await getSampleCountByOffice({
          beginTimestamp: this.timeRange[0],
          endTimestamp: this.timeRange[1],
          officeIds: this.model.nodeList,
          pathogenTypes: this.model.pathogenList
        });

        this.midModel.detectCount = 0;
        this.midModel.pathogenCount = 0;

        this.regionPathogenStatByHalfYearData.splice(
          0,
          Infinity,
          ...res.data.map(e => {
            this.midModel.detectCount += e.bad + e.good;
            this.midModel.pathogenCount += e.bad;
            return e;
          })
        );

        const dict = [
          { lte: 2, rotate: 0, grid: { bottom: 20 } },
          { lte: 3, rotate: 15, grid: { bottom: 20 } },
          { lte: 5, rotate: 30 },
          { lte: 8, rotate: 43 },
          { lte: 9, rotate: 60 },
          { lte: 15, rotate: 70 },
          { lte: 55, rotate: 90 }
        ];

        const len = this.regionPathogenStatByHalfYearData.length;

        const { rotate, grid } = dict.find(e => e.lte >= len);

        this.regionPathogenStatByHalfYearLine.setData(this.regionPathogenStatByHalfYearData, {
          color: ['grey', 'orange'],
          dataset: {
            dimensions: ['officeName', 'good', 'bad']
          },
          grid: grid || { bottom: 0 },
          xAxis: {
            axisLabel: {
              rotate
            }
          },
          series: [
            {
              type: 'bar',
              barMaxWidth: 40,
              barMinWidth: 20,
              stack: 'a',
              name: '阴性'
            },
            {
              type: 'bar',
              barMaxWidth: 40,
              barMinWidth: 20,
              stack: 'a',
              name: '阳性'
            }
          ]
        });
      } catch (error) {
        console.log(error);
      }
    },
    addPointLayer(data) {
      // shapeType 到颜色的映射（对应原 BMapGL.PointShapeLayer 的 match 表达式）
      const colorMap = {
        2: 'red',
        0: 'blue',
        5: 'orange'
      };

      const pointData = data.map(e => ({
        coordinate: e.coordinate,
        name: e.name || '',
        color: colorMap[e.shapeType] || 'green',
        radius: 12
      }));

      this.mapApp.addCircleLayer(pointData);
    }
  },
  watch: {
    model: {
      async handler(val) {
        const diffObj = diffObjects(val, this.old);

        if (isEmpty(diffObj) || !this.loaded) return;

        if (
          'pathogenList' in diffObj ||
          'pathogenListCheckAll' in diffObj ||
          'timeRange' in diffObj
        ) {
          await this.getOfficesState();
        }

        if (
          'nodeRunStateList' in diffObj ||
          'nodeRunStateListCheckAll' in diffObj ||
          'nodeList' in diffObj ||
          'nodeListCheckAll' in diffObj ||
          'pathogenList' in diffObj ||
          'pathogenListCheckAll' in diffObj ||
          'timeRange' in diffObj
        ) {
          this.initNodes(val.nodeList);

          this.initRegionPathogenStatChart();
          this.initPathogenCountChart();
        }

        this.old = cloneDeep(val);
      },
      deep: true
    }
  },
  mounted() {
    if (this.mode === 'monitor') {
      this.resetAllParams();
    }

    this.old = cloneDeep(this.model);

    this.initPathogenCountChart();
    this.initRegionPathogenStatChart();
  }
};
</script>

<style lang="scss">
.RTEAO-situation {
  width: 100%;
  height: 100%;

  --left-width: 28%;

  &_left {
    position: absolute;
    left: var(--gap);
    // top: calc(var(--gap) + var(--header-height));
    top: 9.3rem;

    z-index: 9;
    width: var(--left-width);
    height: calc(100% - var(--gap) * 2 - var(--header-height));

    pointer-events: none;

    display: flex;
    flex-direction: column;
    gap: var(--gap);

    .border-wrap {
      width: 100%;
      height: 40%;

      pointer-events: all;
    }
  }

  &_middle {
    position: absolute;
    left: var(--gap);
    // left: calc(var(--gap) * 2 + var(--left-width));
    top: calc(var(--gap) + var(--header-height));
    z-index: 9;

    pointer-events: none;

    display: flex;
    flex-direction: column;
    gap: calc(var(--gap) / 2);

    user-select: none;
  }
}
</style>
